import { Fragment, useState } from "react"

import { format, parseISO } from "date-fns"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { CopyButton } from "@/components/CopyButton"
import Icon from "@/components/Icon"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Progress } from "@/components/ui/progress"
import {
  type AudienceMiss,
  type IneligibleReason,
  type ReconciledDriver,
  type ReconciledIncentive,
  type TicketStatus,
  useRaiseMissingTickets,
  useReraiseTicket,
} from "@/hooks/useIncentives"
import { cn } from "@/lib/utils"

import { addDaysToDateString, ukToday } from "../../../shared/shift-timing.ts"
import {
  HELD,
  isHeld,
  isRaised,
  isReady,
  pounds,
  raiseSummary,
  routesText,
  splitOwed,
  upTo,
} from "./reconcileFormat"
import { ROUTE_TYPE_LABELS } from "./routeTypeLabels"

const REASON_LABELS: Record<IneligibleReason, string> = {
  not_closed: "Not closed",
  no_payment: "No payment in Moneta",
  route_type: "Route type not ticked",
  route_cycle: "Route cycle not ticked",
  not_in_audience: "Not in the audience that day",
  no_earlier_block: "No block that day or before",
}

const NO_BLOCK = "No block that day"

const SAME_DAY = "Another eligible route that day"

// Fixed widths so the columns line up from one incentive to the next.
const COLUMNS = [
  { label: "Driver", className: "" },
  { label: "Person ID", className: "w-32" },
  { label: "Depot", className: "w-44" },
  { label: "Count", className: "w-32 text-right" },
  { label: "Routes", className: "w-[26%]" },
  { label: "Amount owed", className: "w-32 text-right" },
  { label: "Paid", className: "w-48" },
]

// Matches EntityTable, which can't group drivers or expand their routes. EntityTable's own header
// borders rely on the page clipping it, so here the wrapper draws the outline instead.
const HEAD_CELL =
  "whitespace-nowrap border-b border-palette-jungleGreen200 px-4 py-3 text-left text-xs font-normal text-palette-jungleGreen900"
const CELL = "px-4 py-3 text-sm"

// Each driver falls in exactly one group. Paid but no longer meeting it comes first, since Ops void those by hand.
const GROUPS = [
  {
    label: "Paid, but no longer meets the incentive",
    has: (driver: ReconciledDriver) =>
      !driver.paid && isRaised(driver) && driver.status === "not_met",
    expandAll: true,
  },
  {
    label: "Ready to raise",
    has: isReady,
    expandAll: true,
  },
  {
    label: HELD,
    has: isHeld,
    expandAll: true,
  },
  {
    label: "Raised",
    has: (driver: ReconciledDriver) =>
      !driver.paid && isRaised(driver) && driver.status !== "not_met",
    expandAll: true,
    startsCollapsed: true,
  },
  {
    label: "Didn't meet the incentive",
    has: (driver: ReconciledDriver) =>
      driver.status === "not_met" && !driver.paid && !isRaised(driver),
    expandAll: true,
    startsCollapsed: true,
  },
  {
    label: "Already paid in an earlier week",
    has: (driver: ReconciledDriver) => driver.paid,
    expandAll: false,
    startsCollapsed: true,
  },
]

const shortDay = (date: string) => format(parseISO(date), "d MMM")

const payText = ({ rule }: ReconciledIncentive): string => {
  const onePerDay = rule.one_route_per_day ? ", at most one a day" : ""
  return rule.unit === "per_route"
    ? `${pounds.format(rule.amount)} per route${onePerDay}`
    : `${pounds.format(rule.amount)} for ${routesText(rule.threshold)} ${rule.period === "whole_run" ? "over the whole run" : "a week"}${onePerDay}`
}

const countsText = ({ rule }: ReconciledIncentive): string =>
  `${rule.route_cycles.join(" and ")}: ${rule.route_types.map((type) => ROUTE_TYPE_LABELS[type]).join(", ")}`

const missText = ({ field, value }: AudienceMiss) => `${field} ${value || "none"}`

// Lists what the driver had, not which audience they missed, since each audience can miss on different fields.
const audienceText = (misses: AudienceMiss[]): string => {
  const assumed = misses.filter((miss) => miss.assumed).map(missText)
  return [
    ...misses.filter((miss) => !miss.assumed).map(missText),
    assumed.length > 0 && `no block that day, assumed ${assumed.join(", ")}`,
  ]
    .filter(Boolean)
    .join(", ")
}

const ineligibleNote = ({
  reason,
  route_type,
  route_cycle,
  audience_misses,
}: ReconciledDriver["ineligible_routes"][number]) => {
  if (reason === "route_type") return `${ROUTE_TYPE_LABELS[route_type]}, not ticked`
  if (reason === "route_cycle")
    return route_cycle ? `${route_cycle}, not ticked` : "Route cycle not set in Moneta"
  if (reason === "not_in_audience" && audience_misses?.length > 0)
    return `${REASON_LABELS[reason]}: ${audienceText(audience_misses)}`
  return REASON_LABELS[reason]
}

// A weekly incentive covering the whole week would only repeat the week picker.
const periodText = (incentive: ReconciledIncentive, week: string): string | null => {
  const { rule, end_date, period_start, period_end } = incentive
  const countedTo = `counted to ${shortDay(period_end)}`
  if (rule.period === "whole_run") {
    return end_date
      ? `Whole run, ${shortDay(period_start)} - ${shortDay(end_date)}, ${countedTo}`
      : `Whole run from ${shortDay(period_start)}, ${countedTo}`
  }
  if (period_start === week && period_end === addDaysToDateString(week, 6)) return null
  return `Only ${shortDay(period_start)} - ${shortDay(period_end)} this week`
}

// Each route sits in one list. A waiting route goes there whether it's eligible today or not, since
// Moneta review can still change which.
const routeLists = (driver: ReconciledDriver) => {
  const pending = new Set(driver.pending_route_codes)
  return {
    approved: driver.eligible_route_codes.filter((code) => !pending.has(code)),
    waiting: driver.pending_route_codes,
    ineligible: driver.ineligible_routes.filter((route) => !pending.has(route.route_code)),
  }
}

const substituteText = ({ person }: ReconciledDriver["substitute_routes"][number]) =>
  `driven by substitute ${person}`

// Each substitute once, however many of the driver's routes they drove.
const substitutesText = (driver: ReconciledDriver): string =>
  [...new Set(driver.substitute_routes.map((route) => route.person))].join(", ")

const routeNote = (driver: ReconciledDriver, code: string): string | undefined => {
  const ineligible = driver.ineligible_routes.find((route) => route.route_code === code)
  const substitute = driver.substitute_routes.find((route) => route.route_code === code)
  return (
    [
      ineligible && ineligibleNote(ineligible),
      driver.no_block_route_codes.includes(code) && NO_BLOCK,
      driver.same_day_route_codes.includes(code) && SAME_DAY,
      substitute && substituteText(substitute),
    ]
      .filter(Boolean)
      .join(", ") || undefined
  )
}

// The routes the week totals count as routes to check. Every other reason is in the expanded list.
const routesToCheck = (driver: ReconciledDriver): [string, number][] =>
  (
    [
      [NO_BLOCK, driver.no_block_route_codes.length],
      [
        REASON_LABELS.not_closed,
        driver.ineligible_routes.filter((route) => route.reason === "not_closed").length,
      ],
    ] as [string, number][]
  ).filter(([, count]) => count > 0)

const Marker = ({ className, children }: { className: string; children: string }) => (
  <Badge className={`pointer-events-none border-transparent ${className}`}>{children}</Badge>
)

const RouteChip = ({ code, note }: { code: string; note?: string }) => (
  <span className="inline-flex items-center gap-1 rounded bg-palette-jungleGreen000 py-0.5 pl-2 pr-0.5 text-xs text-palette-jungleGreen900">
    <span>
      {code}
      {note && <span className="text-palette-jungleGreen600"> - {note}</span>}
    </span>
    <CopyButton value={code} label={`route ${code}`} className="h-5 w-5" />
  </span>
)

// Stops the copy toggling the row it sits in.
const RowCopyButton = ({ value, label }: { value: string; label: string }) => (
  <span className="inline-flex" onClick={(event) => event.stopPropagation()}>
    <CopyButton value={value} label={label} />
  </span>
)

const TICKET_DOTS: Record<TicketStatus, string> = {
  Pending: "bg-indicators-yellow",
  Approved: "bg-indicators-green",
  Denied: "bg-indicators-red",
}

const TICKET_LABELS: Record<TicketStatus, string> = {
  Pending: "Pending approval",
  Approved: "Approved",
  Denied: "Denied",
}

const StatusDot = ({ className, label }: { className: string; label: string }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-palette-jungleGreen900">
    <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${className}`} />
    {label}
  </span>
)

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : "Unknown error")

const ReraiseButton = ({ incentiveId, ticketId }: { incentiveId: string; ticketId: string }) => {
  const reraise = useReraiseTicket()
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 px-2 text-xs"
      disabled={reraise.isPending}
      onClick={() =>
        reraise.mutate(
          { incentiveId, ticketId },
          {
            onSuccess: () => toast.success("The ticket is back to Pending approval"),
            onError: (error) =>
              toast.error("The ticket wasn't re-raised", { description: errorMessage(error) }),
          },
        )
      }
    >
      Re-raise
    </Button>
  )
}

// The ticket shows what it was raised for, which a correction in Moneta since can leave behind the live count.
const PaidCell = ({
  driver,
  incentiveId,
  periodOver,
}: {
  driver: ReconciledDriver
  incentiveId: string
  periodOver: boolean
}) => {
  const { ticket } = driver
  if (!ticket) {
    return periodOver && isReady(driver) ? (
      <Marker className="bg-palette-honeycomb100 text-palette-honeycomb800">No ticket</Marker>
    ) : (
      <span className="text-palette-jungleGreen600">-</span>
    )
  }
  if (ticket.status === "failed") {
    return (
      <div className="space-y-1">
        <StatusDot className="bg-indicators-red" label="Failed to raise" />
        <p className="break-words text-xs text-palette-jungleGreen600">{ticket.error}</p>
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <StatusDot
          className={
            ticket.ticket_status ? TICKET_DOTS[ticket.ticket_status] : "bg-palette-jungleGreen300"
          }
          label={ticket.ticket_status ? TICKET_LABELS[ticket.ticket_status] : "Ticket not found"}
        />
        {ticket.ticket_status === "Denied" && (
          <ReraiseButton incentiveId={incentiveId} ticketId={ticket.ticket_id} />
        )}
      </div>
      <p className="text-xs tabular-nums text-palette-jungleGreen600">
        {`${pounds.format(ticket.amount)} for ${routesText(ticket.count)}`}
      </p>
    </div>
  )
}

// Ready is the same set the raise works through, failed rows included.
const CreateMissingTickets = ({
  incentive,
  week,
  periodOver,
}: {
  incentive: ReconciledIncentive
  week: string
  periodOver: boolean
}) => {
  const raise = useRaiseMissingTickets()
  const missing = incentive.drivers.filter(isReady).length
  if (!periodOver || missing === 0) return null
  const label = `Create ${missing} missing ${missing === 1 ? "ticket" : "tickets"}`
  const create = () =>
    raise.mutate(
      { incentiveId: incentive.incentive_id, week },
      {
        onSuccess: (outcome) => {
          const { text, complete } = raiseSummary(outcome)
          if (complete) toast.success(text)
          else toast.warning(text)
        },
        onError: (error) =>
          toast.error("Tickets weren't created", { description: errorMessage(error) }),
      },
    )
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={raise.isPending}>
          {raise.isPending && (
            <Icon name="arrowClockwise" className="mr-2 h-4 w-4 animate-spin text-current" />
          )}
          {raise.isPending ? "Creating tickets..." : label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Create missing tickets for {incentive.name}?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              This raises a ticket for each driver who now meets the incentive and has none, and
              retries any that failed. Each one is Pending approval until Ops review it. It doesn't
              change a ticket already raised, and drivers waiting on Moneta review stay held.
            </span>
            <span className="block font-medium text-palette-jungleGreen900">
              If Finance have already approved the payment cycle, the pay goes into the next one.
              Check with Finance first.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Go back</AlertDialogCancel>
          <AlertDialogAction onClick={create}>Create tickets</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const CountCell = ({
  driver,
  threshold,
}: {
  driver: ReconciledDriver
  threshold: number | null
}) =>
  threshold === null ? (
    <>{driver.settled}</>
  ) : (
    <div className="flex items-center justify-end gap-2">
      <Progress
        value={Math.min(100, (driver.settled / threshold) * 100)}
        aria-label={`${driver.settled} of ${threshold} routes`}
        className="h-1.5 w-12"
      />
      <span className="whitespace-nowrap">
        {driver.settled} / {threshold}
      </span>
    </div>
  )

const DriverRows = ({
  driver,
  threshold,
  incentiveId,
  periodOver,
  expanded,
  onToggle,
}: {
  driver: ReconciledDriver
  threshold: number | null
  incentiveId: string
  periodOver: boolean
  expanded: boolean
  onToggle: () => void
}) => {
  const routes = routeLists(driver)
  const summary = [
    `${routes.approved.length} approved`,
    routes.waiting.length > 0 && `${routes.waiting.length} waiting`,
    routes.ineligible.length > 0 && `${routes.ineligible.length} ineligible`,
  ]
    .filter(Boolean)
    .join(", ")
  const cell = `${CELL} text-palette-jungleGreen700`
  return (
    <>
      <tr
        className="cursor-pointer transition-colors hover:bg-palette-jungleGreen000"
        onClick={onToggle}
      >
        <td className={cell}>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/people?personId=${encodeURIComponent(driver.person_id)}&tab=people`}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "font-medium underline-offset-4 hover:underline",
                !driver.paid && "text-palette-jungleGreen900",
              )}
              onClick={(event) => event.stopPropagation()}
            >
              {driver.person}
            </Link>
            <RowCopyButton value={driver.person} label={`name of ${driver.person}`} />
            {driver.other_incentives_met.length > 0 && (
              <Marker className="bg-palette-jungleGreen100 text-palette-jungleGreen800">
                {`Also met or waiting on ${driver.other_incentives_met.map((name) => `"${name}"`).join(", ")}`}
              </Marker>
            )}
            {driver.substitute_routes.length > 0 && (
              <Marker className="bg-palette-skyBlue100 text-palette-skyBlue900">
                {`Primary, paid for substitute ${substitutesText(driver)}`}
              </Marker>
            )}
          </div>
        </td>
        <td className={cell}>
          <div className="flex items-center gap-1">
            {driver.person_id}
            <RowCopyButton value={driver.person_id} label={`person ID ${driver.person_id}`} />
          </div>
        </td>
        <td className={cell}>{driver.depot ?? "-"}</td>
        <td className={`${cell} text-right tabular-nums`}>
          <CountCell driver={driver} threshold={threshold} />
        </td>
        <td className={cell}>
          <div className="flex flex-wrap items-center gap-2">
            {/* No handler of its own: the click bubbles to the row, which toggles. */}
            <Button
              variant="ghost"
              size="sm"
              className="-ml-3 h-7 gap-1 px-3"
              aria-expanded={expanded}
            >
              <Icon name={expanded ? "caretDown" : "caretRight"} className="h-3 w-3 text-current" />
              {summary}
            </Button>
            {routes.approved.length > 0 && (
              <RowCopyButton
                value={routes.approved.join(", ")}
                label={`eligible and approved routes for ${driver.person}`}
              />
            )}
            {routesToCheck(driver).map(([label, count]) => (
              <Marker key={label} className="bg-palette-honeycomb100 text-palette-honeycomb800">
                {`${count} ${label.toLowerCase()}`}
              </Marker>
            ))}
            {isHeld(driver) && (
              <Marker className="bg-palette-skyBlue100 text-palette-skyBlue900">
                {`${HELD}: ${driver.pending_route_codes.join(", ")}`}
              </Marker>
            )}
          </div>
        </td>
        <td className={`${cell} text-right tabular-nums`}>
          {driver.amount_owed > 0 ? (
            isHeld(driver) ? (
              upTo(driver.amount_owed)
            ) : (
              pounds.format(driver.amount_owed)
            )
          ) : (
            <span className="text-palette-jungleGreen600">Nothing owed</span>
          )}
        </td>
        <td className={cell}>
          {/* Stops a re-raise toggling the row. */}
          <div onClick={(event) => event.stopPropagation()}>
            <PaidCell driver={driver} incentiveId={incentiveId} periodOver={periodOver} />
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={COLUMNS.length} className="px-4 pb-4">
            <div className="space-y-2 rounded-md border border-palette-jungleGreen100 p-3">
              {routes.approved.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                  <span className="mr-1 text-xs text-palette-jungleGreen600">
                    Eligible and approved
                  </span>
                  {routes.approved.map((code) => (
                    <RouteChip key={code} code={code} note={routeNote(driver, code)} />
                  ))}
                </div>
              )}
              {routes.waiting.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                  <span className="mr-1 text-xs text-palette-jungleGreen600">{HELD}</span>
                  {routes.waiting.map((code) => (
                    <RouteChip key={code} code={code} note={routeNote(driver, code)} />
                  ))}
                </div>
              )}
              {routes.ineligible.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                  <span className="mr-1 text-xs text-palette-jungleGreen600">Ineligible</span>
                  {routes.ineligible.map((route) => (
                    <RouteChip
                      key={route.route_code}
                      code={route.route_code}
                      note={routeNote(driver, route.route_code)}
                    />
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const GroupRow = ({
  label,
  open,
  onToggle,
  allExpanded,
  onToggleAll,
}: {
  label: string
  open: boolean
  onToggle: () => void
  allExpanded: boolean
  onToggleAll?: () => void
}) => (
  <tr className="cursor-pointer" onClick={onToggle}>
    <td
      colSpan={COLUMNS.length}
      className="border-t border-palette-jungleGreen100 bg-palette-jungleGreen000 px-4 py-2 text-xs font-medium text-palette-jungleGreen700 [tr:first-child>&]:border-t-0"
    >
      <div className="flex items-center justify-between gap-4">
        {/* No handler of its own: the click bubbles to the row, which toggles. */}
        <Button
          variant="ghost"
          size="sm"
          className="-my-1 -ml-2 h-6 gap-1 px-2 text-xs"
          aria-expanded={open}
        >
          <Icon name={open ? "caretDown" : "caretRight"} className="h-3 w-3 text-current" />
          {label}
        </Button>
        {open && onToggleAll && (
          <Button
            variant="ghost"
            size="sm"
            className="-my-1 h-6 px-2 text-xs"
            onClick={(event) => {
              event.stopPropagation()
              onToggleAll()
            }}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </div>
    </td>
  </tr>
)

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="text-xs text-palette-jungleGreen600">{label}</div>
    <div className="text-xl font-semibold tabular-nums text-palette-jungleGreen900">{value}</div>
  </div>
)

export default function ReconciledIncentiveCard({
  incentive,
  week,
}: {
  incentive: ReconciledIncentive
  week: string
}) {
  const period = periodText(incentive, week)
  const threshold = incentive.rule.unit === "threshold" ? incentive.rule.threshold : null
  const owed = splitOwed([incentive])
  const periodOver = incentive.period_end < ukToday()
  const driversOwed = incentive.drivers.filter(
    (driver) => driver.status !== "not_met" && !driver.paid && !isRaised(driver),
  ).length
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [closedGroups, setClosedGroups] = useState<Set<string>>(
    () => new Set(GROUPS.filter((group) => group.startsCollapsed).map((group) => group.label)),
  )

  const toggleGroup = (label: string) =>
    setClosedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })

  const toggle = (peopleIds: string[], open: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      for (const id of peopleIds) {
        if (open) next.add(id)
        else next.delete(id)
      }
      return next
    })

  return (
    <Collapsible defaultOpen={incentive.drivers.length > 0} asChild>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <div className="flex items-start gap-2">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Drivers for ${incentive.name}`}
                className="group h-6 w-6"
              >
                <Icon
                  name="caretDown"
                  className="h-4 w-4 text-current transition-transform group-data-[state=closed]:-rotate-90"
                />
              </Button>
            </CollapsibleTrigger>
            <div className="space-y-1">
              <Link
                to={`/incentives/${incentive.incentive_id}`}
                className="text-base font-semibold text-palette-jungleGreen900 underline-offset-4 hover:underline"
              >
                {incentive.name}
              </Link>
              {period && <p className="text-sm text-palette-jungleGreen600">{period}</p>}
              <p className="text-sm text-palette-jungleGreen600">{payText(incentive)}</p>
              <p className="text-sm text-palette-jungleGreen600">{countsText(incentive)}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex gap-6 text-right">
              <Stat label="Drivers owed" value={String(driversOwed)} />
              <Stat label="Ready to raise" value={pounds.format(owed.ready)} />
              {owed.held > 0 && <Stat label={HELD} value={upTo(owed.held)} />}
              {owed.raised > 0 && <Stat label="Raised" value={pounds.format(owed.raised)} />}
            </div>
            <CreateMissingTickets incentive={incentive} week={week} periodOver={periodOver} />
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            {incentive.drivers.length === 0 ? (
              <p className="text-sm text-palette-jungleGreen600">
                No driver in the audience had a route in this period.
              </p>
            ) : (
              <div className="overflow-auto rounded-lg border border-palette-jungleGreen200">
                <table className="w-full min-w-[1080px] table-fixed border-separate border-spacing-0">
                  <thead className="bg-palette-jungleGreen000">
                    <tr>
                      {COLUMNS.map((column) => (
                        <th key={column.label} className={cn(HEAD_CELL, column.className)}>
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {GROUPS.map((group) => ({
                      ...group,
                      drivers: incentive.drivers.filter(group.has),
                    }))
                      .filter((group) => group.drivers.length > 0)
                      .map((group) => {
                        const ids = group.drivers.map((driver) => driver.people_id)
                        const allExpanded = ids.every((id) => expanded.has(id))
                        const open = !closedGroups.has(group.label)
                        return (
                          <Fragment key={group.label}>
                            <GroupRow
                              label={`${group.label} (${group.drivers.length})`}
                              open={open}
                              onToggle={() => toggleGroup(group.label)}
                              allExpanded={allExpanded}
                              onToggleAll={
                                group.expandAll ? () => toggle(ids, !allExpanded) : undefined
                              }
                            />
                            {open &&
                              group.drivers.map((driver) => (
                                <DriverRows
                                  key={driver.people_id}
                                  driver={driver}
                                  threshold={threshold}
                                  incentiveId={incentive.incentive_id}
                                  periodOver={periodOver}
                                  expanded={expanded.has(driver.people_id)}
                                  onToggle={() =>
                                    toggle([driver.people_id], !expanded.has(driver.people_id))
                                  }
                                />
                              ))}
                          </Fragment>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
